import { useQueryClient } from '@tanstack/react-query';
import { PAY_METHOD_LABELS, PAY_METHODS, type PayMethod, type SupplierAccountDTO, type SupplierInvoiceDTO } from '@bifeai/shared';
import { Button, Callout, Dialog, Field, Input, Select, Textarea, useToast } from '@bifeai/ui';
import { HandCoins } from 'lucide-react';
import { useMemo, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../lib/api';
import { formatDate, formatMoney } from '../../lib/format';
import { centsToPesosInput, pesosToCents, todayIso } from '../../lib/units';

export const invoiceLabel = (i: SupplierInvoiceDTO) =>
  i.invoiceNumber ? `${i.invoiceType ? `${i.invoiceType} ` : ''}${i.invoiceNumber}` : `Compra del ${formatDate(i.date)}`;

export function dueText(i: SupplierInvoiceDTO): { text: string; tone: 'danger' | 'warning' | null } {
  if (i.dueDate === null || i.overdueDays === null) return { text: 'Sin vencimiento', tone: null };
  if (i.overdueDays > 0) return { text: `Vencida hace ${i.overdueDays} ${i.overdueDays === 1 ? 'día' : 'días'}`, tone: 'danger' };
  if (i.overdueDays === 0) return { text: 'Vence hoy', tone: 'warning' };
  if (i.overdueDays >= -7) return { text: `Vence el ${formatDate(i.dueDate)}`, tone: 'warning' };
  return { text: `Vence el ${formatDate(i.dueDate)}`, tone: null };
}

/**
 * Pago (o nota de crédito) a un proveedor. Se puede tildar a qué facturas va; si no, se paga
 * primero la más vieja. En efectivo sale de la caja abierta.
 */
export function PayDialog({ account, kind, onClose }: { account: SupplierAccountDTO; kind: 'PAYMENT' | 'CREDIT_NOTE'; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [amount, setAmount] = useState(kind === 'PAYMENT' ? centsToPesosInput(account.balanceCents) : '');
  const [method, setMethod] = useState<PayMethod>('TRANSFER');
  const [date, setDate] = useState(todayIso());
  const [reference, setReference] = useState('');
  const [checkDue, setCheckDue] = useState('');
  const [notes, setNotes] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const cents = pesosToCents(amount || '0') ?? 0;

  // Reparto: entre las tildadas (o todas), la que vence antes primero.
  const pool = account.invoices.filter((i) => !picked.size || picked.has(i.purchaseId));
  const poolTotal = pool.reduce((a, i) => a + i.pendingCents, 0);
  const allocation = useMemo(() => {
    let left = cents;
    return pool.map((i) => {
      const take = Math.max(0, Math.min(left, i.pendingCents));
      left -= take;
      return { invoice: i, amount: take };
    });
  }, [cents, pool]);

  function toggle(id: string) {
    setPicked((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      const total = account.invoices.filter((i) => n.has(i.purchaseId)).reduce((a, i) => a + i.pendingCents, 0);
      if (kind === 'PAYMENT') setAmount(centsToPesosInput(n.size ? total : account.balanceCents));
      return n;
    });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (cents <= 0) return setError('Poné el monto.');
    if (cents > poolTotal) return setError(`Las facturas ${picked.size ? 'elegidas suman' : 'pendientes suman'} ${formatMoney(poolTotal)}.`);
    if (kind === 'PAYMENT' && method === 'CHECK' && !checkDue) return setError('Poné la fecha en que cobran el cheque.');
    if (kind === 'CREDIT_NOTE' && notes.trim().length < 3) return setError('Contá el motivo de la nota de crédito.');
    setBusy(true);
    setError(null);
    try {
      await api.post('/supplier-payments', {
        supplierId: account.supplierId,
        kind,
        method: kind === 'PAYMENT' ? method : null,
        date,
        amountCents: cents,
        reference: reference.trim() || null,
        checkDueDate: kind === 'PAYMENT' && method === 'CHECK' ? checkDue : null,
        notes: notes.trim() || null,
        allocations: allocation.filter((a) => a.amount > 0).map((a) => ({ purchaseId: a.invoice.purchaseId, amountCents: a.amount })),
      });
      await Promise.all(['payables', 'suppliers', 'purchases', 'cash', 'notifications'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
      toast.success(kind === 'PAYMENT' ? 'Pago registrado' : 'Nota de crédito registrada', `${account.supplierName}: ${formatMoney(cents)}`);
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title={kind === 'PAYMENT' ? `Pagar a ${account.supplierName}` : `Nota de crédito de ${account.supplierName}`}
      description={
        kind === 'PAYMENT'
          ? `Se deben ${formatMoney(account.balanceCents)}. Tildá las facturas que pagás; si no tildás ninguna, se paga primero la que vence antes.`
          : 'Cuando el proveedor te descuenta algo (mercadería en mal estado, diferencia de peso, bonificación). Baja la deuda.'
      }
      wide
    >
      <form className="bf-form" onSubmit={submit} noValidate>
        <div className="bf-form-row">
          <Field label="Monto">
            <Input className="bf-input--lg" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
          </Field>
          {kind === 'PAYMENT' && (
            <Field label="Cómo" hint={method === 'CASH' ? 'Sale de la caja abierta.' : undefined}>
              <Select value={method} onChange={(e) => setMethod(e.target.value as PayMethod)}>
                {PAY_METHODS.map((m) => (
                  <option key={m} value={m}>
                    {PAY_METHOD_LABELS[m]}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          <Field label="Fecha">
            <Input type="date" value={date} max={todayIso()} onChange={(e) => e.target.value && setDate(e.target.value)} />
          </Field>
        </div>
        <div className="bf-form-row">
          <Field
            label={kind === 'CREDIT_NOTE' ? 'Número de nota de crédito' : method === 'CHECK' ? 'Número de cheque' : 'Número de operación (opcional)'}
          >
            <Input value={reference} onChange={(e) => setReference(e.target.value)} />
          </Field>
          {kind === 'PAYMENT' && method === 'CHECK' && (
            <Field label="Fecha de cobro" hint="Ese día la plata tiene que estar en el banco.">
              <Input type="date" value={checkDue} min={date} onChange={(e) => setCheckDue(e.target.value)} />
            </Field>
          )}
        </div>
        {account.invoices.length > 0 && (
          <div className="bf-scroll-box bf-cut-table">
            <table className="bf-table">
              <thead>
                <tr>
                  <th />
                  <th>Factura</th>
                  <th>Vencimiento</th>
                  <th style={{ textAlign: 'right' }}>Debe</th>
                  <th style={{ textAlign: 'right' }}>Se imputa</th>
                </tr>
              </thead>
              <tbody>
                {account.invoices.map((i) => {
                  const a = allocation.find((x) => x.invoice.purchaseId === i.purchaseId);
                  const d = dueText(i);
                  return (
                    <tr key={i.purchaseId}>
                      <td style={{ width: 32 }}>
                        <input
                          type="checkbox"
                          checked={picked.has(i.purchaseId)}
                          onChange={() => toggle(i.purchaseId)}
                          aria-label={`Pagar ${invoiceLabel(i)}`}
                        />
                      </td>
                      <td>
                        <span className="bf-cell-main">{invoiceLabel(i)}</span>
                        <div className="bf-cell-sub">{formatDate(i.date)}</div>
                      </td>
                      <td className={d.tone === 'danger' ? 'bf-text-danger' : d.tone === 'warning' ? 'bf-text-warning' : 'bf-cell-sub'}>{d.text}</td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {formatMoney(i.pendingCents)}
                      </td>
                      <td className="bf-num" style={{ textAlign: 'right', fontWeight: 600 }}>
                        {a && a.amount > 0 ? formatMoney(a.amount) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <Field label={kind === 'CREDIT_NOTE' ? 'Motivo' : 'Notas (opcional)'}>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        {error && <Callout tone="danger">{error}</Callout>}
        <div className="bf-form-actions">
          <Button onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" icon={<HandCoins />} loading={busy}>
            {kind === 'PAYMENT' ? `Pagar ${cents ? formatMoney(cents) : ''}` : 'Registrar nota de crédito'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
